/**
 * Aggressive cache busting utility for shared hosting environments
 * Adds multiple parameters to force cache refresh
 */

export const bustImageCache = (imagePath: string): string => {
  if (!imagePath) return imagePath;
  
  // Skip if already has cache busting parameters
  if (imagePath.includes('?') || imagePath.includes('&')) {
    return imagePath;
  }
  
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const version = process.env.REACT_APP_VERSION || '1.0.0';
  
  // Add multiple cache busting parameters
  const separator = imagePath.includes('?') ? '&' : '?';
  return `${imagePath}${separator}v=${version}&t=${timestamp}&r=${random}&cb=1`;
};

export const bustSliderImageCache = (imagePath: string): string => {
  if (!imagePath) return imagePath;
  
  // For slider images, be even more aggressive
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const sessionId = sessionStorage.getItem('sessionId') || Math.random().toString(36).substring(2, 15);
  
  // Store session ID for consistency
  if (!sessionStorage.getItem('sessionId')) {
    sessionStorage.setItem('sessionId', sessionId);
  }
  
  const separator = imagePath.includes('?') ? '&' : '?';
  return `${imagePath}${separator}slider=1&t=${timestamp}&r=${random}&s=${sessionId}&cb=2`;
};

export const forceImageRefresh = (imageElement: HTMLImageElement): void => {
  if (!imageElement) return;
  
  const currentSrc = imageElement.src;
  const newSrc = bustImageCache(currentSrc.split('?')[0]);
  
  // Force reload by temporarily changing src
  imageElement.src = '';
  setTimeout(() => {
    imageElement.src = newSrc;
  }, 10);
};

export const refreshAllSliderImages = (): void => {
  const sliderImages = document.querySelectorAll('img[src*="/slider/"]');
  sliderImages.forEach((img) => {
    if (img instanceof HTMLImageElement) {
      forceImageRefresh(img);
    }
  });
};

// Auto-refresh slider images every 5 minutes in development
if (process.env.NODE_ENV === 'development') {
  setInterval(refreshAllSliderImages, 5 * 60 * 1000);
}

// Image cache busting utility
export const getImageCacheBuster = (imagePath: string): string => {
  const separator = imagePath.includes('?') ? '&' : '?';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(7);
  return `${imagePath}${separator}v=${timestamp}&r=${random}`;
};

// Cache buster for slider images specifically
export const getSliderImageCacheBuster = (imagePath: string): string => {
  const separator = imagePath.includes('?') ? '&' : '?';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(7);
  return `${imagePath}${separator}slider=${timestamp}&r=${random}`;
};

// Force reload all images on the page
export const reloadAllImages = (): void => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    const imgElement = img as HTMLImageElement;
    const src = imgElement.getAttribute('src');
    if (src) {
      const newSrc = getImageCacheBuster(src);
      imgElement.src = newSrc;
    }
  });
};

// Force reload slider images specifically
export const reloadSliderImages = (): void => {
  const sliderImages = document.querySelectorAll('img[src*="slider/"]');
  sliderImages.forEach((img, index) => {
    const imgElement = img as HTMLImageElement;
    const src = imgElement.getAttribute('src');
    if (src) {
      const newSrc = getSliderImageCacheBuster(src);
      imgElement.src = newSrc;
    }
  });
};

// Force reload by selector
export const reloadImagesBySelector = (selector: string): void => {
  const images = document.querySelectorAll(selector);
  images.forEach((img, index) => {
    const imgElement = img as HTMLImageElement;
    const src = imgElement.getAttribute('src');
    if (src) {
      const newSrc = getImageCacheBuster(src);
      imgElement.src = newSrc;
    }
  });
};

// Preload image with cache buster
export const preloadImage = (imagePath: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = getImageCacheBuster(imagePath);
  });
};

// Clear image cache in browser
export const clearImageCache = (): void => {
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        if (name.includes('image') || name.includes('static')) {
          caches.delete(name);
        }
      });
    });
  }
};

// Force browser to reload images by changing src
export const forceImageReload = (): void => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    const imgElement = img as HTMLImageElement;
    const originalSrc = imgElement.src;
    if (originalSrc) {
      // Temporarily set to empty to force reload
      imgElement.src = '';
      setTimeout(() => {
        imgElement.src = getImageCacheBuster(originalSrc);
      }, 100);
    }
  });
}; 